// Iteration 12A — Pine→TSL importer GATED-BETA feature flag + kill switches.
//
// FAIL-CLOSED by design: the importer beta is OFF unless a user is explicitly granted access. The order
// of precedence (most-authoritative first):
//   1. local kill switch    (localStorage `pine_importer_kill` = '1')  → hard OFF for this browser (dev/emergency)
//   2. REMOTE fleet kill     (VITE_PINE_REMOTE_CONFIG_URL → pineImporterKill) → instant OFF for EVERYONE;
//                            unread/unreadable → OFF (fail closed). Overrides admin / allowlist / opt-in / env.
//   3. global env override  (VITE_PINE_IMPORTER_BETA = 'off' | 'on')   → force OFF / ON for everyone
//   4. admin                (isAdmin)                                   → ON
//   5. email allowlist      (VITE_PINE_IMPORTER_ALLOWLIST, comma list) → ON for listed emails
//   6. local opt-in         (localStorage `pine_importer_optin` = '1') → ON (dev / QA self-serve)
//   7. default                                                          → OFF (not in beta)
//
// No converter code runs for an ungated user — the UI shows the corresponding state instead.

import type { PineRemoteKillState } from './pineRemoteKill';
import { publicEnv } from './clientEnv';

export type PineImporterGate = { enabled: boolean; reason: PineGateReason };
export type PineGateReason =
  | 'kill_switch' | 'remote_kill' | 'remote_unavailable' | 'disabled_globally' | 'enabled_globally'
  | 'admin' | 'allowlisted' | 'local_optin' | 'not_in_beta';

export const PINE_KILL_KEY = 'pine_importer_kill';
export const PINE_OPTIN_KEY = 'pine_importer_optin';

function ls(key: string): string | null {
  try { return typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null; } catch { return null; }
}

export function pineImporterGate(opts: { isAdmin?: boolean | null; email?: string | null; remote?: PineRemoteKillState }): PineImporterGate {
  // 1. Local per-browser kill (dev / emergency override).
  if (ls(PINE_KILL_KEY) === '1') return { enabled: false, reason: 'kill_switch' };
  // 2. Remote FLEET kill — overrides everything below. Fails closed while unread / unreadable.
  const remote = opts.remote;
  if (remote?.configured) {
    if (!remote.loaded || remote.error) return { enabled: false, reason: 'remote_unavailable' };
    if (remote.killed) return { enabled: false, reason: 'remote_kill' };
  }
  const global = (publicEnv('VITE_PINE_IMPORTER_BETA') ?? '').toLowerCase();
  if (global === 'off') return { enabled: false, reason: 'disabled_globally' };
  if (global === 'on') return { enabled: true, reason: 'enabled_globally' };
  if (opts.isAdmin) return { enabled: true, reason: 'admin' };
  const allow = (publicEnv('VITE_PINE_IMPORTER_ALLOWLIST') ?? '').split(',').map((s) => s.trim().toLowerCase()).filter(Boolean);
  if (opts.email && allow.includes(opts.email.toLowerCase())) return { enabled: true, reason: 'allowlisted' };
  if (ls(PINE_OPTIN_KEY) === '1') return { enabled: true, reason: 'local_optin' };
  return { enabled: false, reason: 'not_in_beta' };
}

/** Engage the kill switch for this browser (instant, no deploy). */
export function engagePineKillSwitch(on: boolean): void {
  try { if (on) localStorage.setItem(PINE_KILL_KEY, '1'); else localStorage.removeItem(PINE_KILL_KEY); } catch { /* ignore */ }
}
